'use client'

import { MinusIcon, PlusIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { useNumberFormat } from '@/hooks/use-number-format'
import type { PerceptChartSettings } from '@/types/dashboard'

type PrecisionMode = NonNullable<PerceptChartSettings['precisionMode']>

const MIN_DECIMALS = 1
const MAX_DECIMALS = 8
/** What a fixed precision starts at when none was chosen yet. */
export const DEFAULT_DECIMALS = 2
// A price with enough digits on both sides to show what each setting does to it.
const PREVIEW_PRICE = 81234.56789

/**
 * Percept's price precision as a mode switch plus, for a fixed count, a −/+ stepper — rather
 * than ten near-identical menu rows. The preview shows the effect before looking at the chart.
 */
export function PrecisionSettingContent({
  settings,
  onChange,
}: {
  settings: PerceptChartSettings
  onChange: (patch: PerceptChartSettings) => void
}) {
  const t = useTranslations('trade.chartSettings')
  const fmt = useNumberFormat()
  const mode: PrecisionMode = settings.precisionMode ?? 'default'
  const decimals = settings.decimalPlaces ?? DEFAULT_DECIMALS

  const setDecimals = (next: number) =>
    onChange({
      precisionMode: 'manual',
      decimalPlaces: Math.min(MAX_DECIMALS, Math.max(MIN_DECIMALS, next)),
    })

  const previewDigits = mode === 'integer' ? 0 : decimals

  return (
    <div className="flex w-60 flex-col gap-3 p-2">
      <SegmentedControl<PrecisionMode>
        size="xs"
        fill
        value={mode}
        onChange={(next) =>
          onChange(
            next === 'manual'
              ? { precisionMode: 'manual', decimalPlaces: decimals }
              : { precisionMode: next }
          )
        }
        options={[
          { value: 'default', label: t('precisionModes.default') },
          { value: 'integer', label: t('precisionModes.integer') },
          { value: 'manual', label: t('precisionModes.fixed') },
        ]}
      />

      {mode === 'manual' && (
        <div className="flex items-center justify-between gap-2 px-1">
          <span className="text-sm tracking-tight">{t('precisionDecimals')}</span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="iconXs"
              aria-label={t('precisionFewer')}
              disabled={decimals <= MIN_DECIMALS}
              onClick={() => setDecimals(decimals - 1)}
            >
              <MinusIcon />
            </Button>
            <span className="w-6 text-center text-sm" aria-live="polite">
              {decimals}
            </span>
            <Button
              type="button"
              variant="outline"
              size="iconXs"
              aria-label={t('precisionMore')}
              disabled={decimals >= MAX_DECIMALS}
              onClick={() => setDecimals(decimals + 1)}
            >
              <PlusIcon />
            </Button>
          </div>
        </div>
      )}

      <div className="border-foreground/10 flex items-baseline justify-between gap-2 border-t px-1 pt-2">
        <span className="text-foreground/40 text-xs">{t('precisionPreview')}</span>
        <span className="text-foreground/80 text-sm">
          {mode === 'default'
            ? t('precisionDefaultHint')
            : fmt.number(PREVIEW_PRICE, {
                minimumFractionDigits: previewDigits,
                maximumFractionDigits: previewDigits,
              })}
        </span>
      </div>
    </div>
  )
}
